import { stopDispatcher } from "@/lib/upload-dispatcher";

const KEEP_CACHES = new Set(["vroom-i18n", "vroom-static"]);

const TARGET_DBS = ["vroom-worker", "vroom-tanstack", "vroom-upload-queue"];

/**
 * Minimal interface for the parts of QueryClient we touch during sign-out.
 * Avoids importing QueryClient from @tanstack/react-query at this level.
 */
interface QueryClientLike {
  cancelQueries(): void;
  clear(): void;
}

export interface WipeLocalStateOpts {
  userId: string;
  queryClient?: QueryClientLike;
  persisterRemove?: () => Promise<void>;
}

/**
 * Wipes all per-user local state on sign-out:
 *   1. Cancel in-flight queries (prevents stale writes after clear)
 *   2. Stop the upload dispatcher (prevents re-creating the queue DB after deletion)
 *   3. Remove the query persister snapshot
 *   4. Clear the in-memory query cache
 *   5. Delete all per-user IndexedDB databases
 *   6. Clear non-essential caches (keeps vroom-i18n + vroom-static)
 *   7. Clear sessionStorage marker
 */
export async function wipeLocalState(opts: WipeLocalStateOpts): Promise<void> {
  opts.queryClient?.cancelQueries();

  stopDispatcher();

  await opts.persisterRemove?.();

  opts.queryClient?.clear();

  for (const name of TARGET_DBS) {
    await deleteDb(name);
  }

  if ("caches" in self) {
    const keys = await caches.keys();
    await Promise.all(keys.filter((k) => !KEEP_CACHES.has(k)).map((k) => caches.delete(k)));
  }

  try {
    sessionStorage.removeItem("vroom.lastUserId");
  } catch {
    // sessionStorage may be unavailable in certain contexts — ignore
  }
}

function deleteDb(name: string): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}
